/* START_MODULE_CONTRACT
 * PURPOSE: Frontend API client для фич проекта (CRUD, archive, reorder).
 * SCOPE: HTTP-вызовы /api/projects/{id}/features.
 * DEPENDS: M-FE-API
 * LINKS: M-FE-API, V-M-FE-API, M-FEATURE
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * getFeatures - список фич проекта.
 * createFeature - создать фичу (name, startDate, status, estimates).
 * updateFeature - обновить фичу (name, startDate, status, estimates).
 * archiveFeature - архивировать фичу.
 * deleteFeature - удалить фичу.
 * reorderFeatures - изменить порядок фич.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - BUG_FIX: прокидывать status в create/update (UC-020).
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: function contracts и парные END_METHOD.
 * END_CHANGE_SUMMARY
 */

import apiClient from './apiClient';

/* START_METHOD_getFeatures */
/* START_CONTRACT: getFeatures
 * PURPOSE: Получить список фич проекта.
 * INPUTS: { projectId: number|string, signal: AbortSignal }
 * OUTPUTS: { Promise<AxiosResponse> - GET features }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE
 * END_CONTRACT: getFeatures
 */
export function getFeatures(projectId, signal) {
  return apiClient.get(`/api/projects/${projectId}/features`, { signal });
}
/* END_METHOD_getFeatures */

/* START_METHOD_createFeature */
/* START_CONTRACT: createFeature
 * PURPOSE: Создать фичу с оценками и статусом BACKLOG|IN_PROGRESS.
 * INPUTS: { projectId: number|string, payload: { name, startDate, status, estimates } }
 * OUTPUTS: { Promise<AxiosResponse> - POST feature }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE, UC-020
 * END_CONTRACT: createFeature
 */
export function createFeature(projectId, { name, startDate, status, estimates }) {
  // BUG_FIX_CONTEXT: Previous create body omitted status; backend defaulted to IN_PROGRESS.
  // Dialogs already send status — must forward it in the JSON body.
  return apiClient.post(`/api/projects/${projectId}/features`, {
    name,
    startDate,
    status,
    estimates,
  });
}
/* END_METHOD_createFeature */

/* START_METHOD_updateFeature */
/* START_CONTRACT: updateFeature
 * PURPOSE: Обновить фичу, включая статус BACKLOG|IN_PROGRESS.
 * INPUTS: { projectId, featureId, payload: { name, startDate, status, estimates } }
 * OUTPUTS: { Promise<AxiosResponse> - PUT feature }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE, UC-020
 * END_CONTRACT: updateFeature
 */
export function updateFeature(projectId, featureId, { name, startDate, status, estimates }) {
  // BUG_FIX_CONTEXT: Same omission as create — status was stripped before PUT.
  return apiClient.put(`/api/projects/${projectId}/features/${featureId}`, {
    name,
    startDate,
    status,
    estimates,
  });
}
/* END_METHOD_updateFeature */

/* START_METHOD_archiveFeature */
/* START_CONTRACT: archiveFeature
 * PURPOSE: Архивировать фичу (отдельно от удаления).
 * INPUTS: { projectId, featureId }
 * OUTPUTS: { Promise<AxiosResponse> - POST /archive }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE
 * END_CONTRACT: archiveFeature
 */
export function archiveFeature(projectId, featureId) {
  return apiClient.post(`/api/projects/${projectId}/features/${featureId}/archive`);
}
/* END_METHOD_archiveFeature */

/* START_METHOD_deleteFeature */
/* START_CONTRACT: deleteFeature
 * PURPOSE: Удалить фичу.
 * INPUTS: { projectId, featureId }
 * OUTPUTS: { Promise<AxiosResponse> - DELETE feature }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE
 * END_CONTRACT: deleteFeature
 */
export function deleteFeature(projectId, featureId) {
  return apiClient.delete(`/api/projects/${projectId}/features/${featureId}`);
}
/* END_METHOD_deleteFeature */

/* START_METHOD_reorderFeatures */
/* START_CONTRACT: reorderFeatures
 * PURPOSE: Изменить порядок фич в проекте.
 * INPUTS: { projectId, featureIds: Array<number> - новый порядок }
 * OUTPUTS: { Promise<AxiosResponse> - PUT /reorder }
 * SIDE_EFFECTS: HTTP-запрос к backend.
 * LINKS: M-FEATURE
 * END_CONTRACT: reorderFeatures
 */
export function reorderFeatures(projectId, featureIds) {
  return apiClient.put(`/api/projects/${projectId}/features/reorder`, { featureIds });
}
/* END_METHOD_reorderFeatures */
